import {
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    Building,
    CircleDot,
    Layers,
    MapPin,
    TrendingDown,
    TrendingUp,
} from 'lucide-react';
import {
    Fragment,
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import {
    DateRangePicker,
    type DateRangePickerValue,
} from '@apps/web/components/ui/date_range_picker';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useAuthorizationsFilterPersistence } from '@apps/web/hooks/use_authorizations_filter_persistence';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getDateStringInTimezone } from '@apps/web/lib/date_utils';
import {
    appTableFeatures,
    type ColumnDef,
    type ColumnFiltersState,
    useTable,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    useAuthorizationsAttentionList,
    useAuthorizationsFilterOptions,
    useAuthorizationsLeaderboard,
    useAuthorizationsMonthlyRollup,
    useAuthorizationsSummary,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type AttentionType = 'over_delivered' | 'under_scheduled';

const TRACE_REASON_SUMMARY = 'authorizations-page-summary';
const TRACE_REASON_ROLLUP = 'authorizations-page-rollup';
const TRACE_REASON_ATTENTION = 'authorizations-page-attention';
const TRACE_REASON_LEADERBOARD = 'authorizations-page-leaderboard';
const ROLLUP_MONTHS = 12;
const ATTENTION_PAGE_SIZE = 25;
const MATCH_NONE_SENTINEL = '__phoebe_match_none__';

type FilterRow = Record<string, never>;

// Headless table used purely as filter state for DataTableToolbar; there is
// no row data — every facet supplies predefinedOptions from filter-options.
const EMPTY_FILTER_ROWS: Array<FilterRow> = [];
const AUTH_FILTER_COLUMNS: Array<ColumnDef<FilterRow>> = [
    { id: 'locations', accessorFn: () => null },
    { id: 'states', accessorFn: () => null },
    { id: 'statuses', accessorFn: () => null },
    { id: 'service_lines', accessorFn: () => null },
];

function getFacetSelection(
    columnFilters: ColumnFiltersState,
    columnId: string,
): { include: Array<string>; exclude: Array<string> } | undefined {
    const value = columnFilters.find((f) => f.id === columnId)?.value;
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const { include, exclude } = value as {
        include?: Array<unknown>;
        exclude?: Array<unknown>;
    };
    return {
        include: (include ?? []).map(String),
        exclude: (exclude ?? []).map(String),
    };
}

// Translates a facet's include/exclude selection into the include-only list
// the API accepts: excludes become the complement of the filter-options
// universe (same approach as the shifts page). Returns undefined for "no
// filter".
function resolveFacetParam(
    selection: { include: Array<string>; exclude: Array<string> } | undefined,
    universe: ReadonlyArray<string> | undefined,
): Array<string> | undefined {
    if (selection && selection.include.length > 0) {
        // Sort so the SWR cache key is stable regardless of click order.
        return [...selection.include].sort();
    }
    if (selection && selection.exclude.length > 0 && universe) {
        const complement = universe.filter(
            (v) => !selection.exclude.includes(v),
        );
        return complement.length > 0
            ? complement.sort()
            : [MATCH_NONE_SENTINEL];
    }
    return undefined;
}

function titleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function toNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined) {
        return 0;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatHours(value: string | number | null | undefined): string {
    return toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function formatPercent(value: string | number | null | undefined): string {
    return `${toNumber(value).toFixed(1)}%`;
}

function formatMonth(monthStartIso: string): string {
    const parsed = new Date(`${monthStartIso}T00:00:00`);
    return parsed.toLocaleString(undefined, {
        month: 'short',
        year: 'numeric',
    });
}

function formatBucketRange(startIso: string, endIso: string): string {
    const start = new Date(`${startIso}T00:00:00`);
    const end = new Date(`${endIso}T00:00:00`);
    const fmt = (d: Date) =>
        d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
        });
    return `${fmt(start)} – ${fmt(end)}`;
}

function formatRelativeSync(iso: string | null | undefined): string {
    if (iso === null || iso === undefined) {
        return 'Never synced';
    }
    const synced = new Date(iso);
    const diffMs = Date.now() - synced.getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) {
        return 'Synced recently';
    }
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 60) {
        return `Last synced ${minutes} min ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
        return `Last synced ${hours} h ago`;
    }
    return `Last synced ${Math.floor(hours / 24)} d ago`;
}

function monthToDateRange(
    now: Date,
    timezone: string,
): { from: string; to: string } {
    const today = getDateStringInTimezone(now, timezone);
    return { from: `${today.slice(0, 7)}-01`, to: today };
}

function utilColorClass(value: number): string {
    if (value >= 70) {
        return 'text-emerald-600';
    }
    if (value >= 50) {
        return 'text-amber-600';
    }
    return 'text-red-600';
}

export function AuthorizationsPage() {
    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const now = useMemo(() => new Date(), []);

    const [dateRange, setDateRange] = useState<DateRangePickerValue>(() =>
        monthToDateRange(now, timezone),
    );
    const userAdjustedRange = useRef(false);

    // The org timezone resolves asynchronously; re-derive the month-to-date
    // default once it lands, unless the user has already picked a range.
    useEffect(() => {
        if (!userAdjustedRange.current) {
            setDateRange(monthToDateRange(now, timezone));
        }
    }, [now, timezone]);
    const [leaderboardView, setLeaderboardView] = useState<'month' | 'days'>(
        'month',
    );
    const { columnFilters, setColumnFilters, isHydrated } =
        useAuthorizationsFilterPersistence();
    const [attentionModal, setAttentionModal] = useState<AttentionType | null>(
        null,
    );
    const [attentionPage, setAttentionPage] = useState(1);

    const { data: filterOptions } =
        useAuthorizationsFilterOptions(organizationId);

    const filterTable = useTable({
        features: appTableFeatures,
        data: EMPTY_FILTER_ROWS,
        columns: AUTH_FILTER_COLUMNS,
        state: { columnFilters },
        onColumnFiltersChange: setColumnFilters,
        manualFiltering: true,
    });

    useEffect(() => {
        setAttentionPage(1);
    }, [columnFilters, dateRange]);

    const filters = useMemo(() => {
        if (!filterOptions) {
            return {};
        }
        return {
            locations: resolveFacetParam(
                getFacetSelection(columnFilters, 'locations'),
                filterOptions.locations,
            ),
            states: resolveFacetParam(
                getFacetSelection(columnFilters, 'states'),
                filterOptions.states,
            ),
            // A cleared statuses facet means "all statuses": send the full
            // universe explicitly, since omitting the param re-applies the
            // backend default of active/pending/closed.
            statuses:
                resolveFacetParam(
                    getFacetSelection(columnFilters, 'statuses'),
                    filterOptions.statuses,
                ) ?? [...filterOptions.statuses].sort(),
            serviceLines: resolveFacetParam(
                getFacetSelection(columnFilters, 'service_lines'),
                filterOptions.service_lines,
            ),
        };
    }, [columnFilters, filterOptions]);
    const dataReady = filterOptions !== undefined && isHydrated;
    const dataOrganizationId = dataReady ? organizationId : null;
    const { data: summary, isLoading: summaryFetching } =
        useAuthorizationsSummary(
            dataOrganizationId,
            dateRange.from,
            dateRange.to,
            { reason: TRACE_REASON_SUMMARY },
            filters,
        );
    const { data: rollup, isLoading: rollupFetching } =
        useAuthorizationsMonthlyRollup(
            dataOrganizationId,
            ROLLUP_MONTHS,
            { reason: TRACE_REASON_ROLLUP },
            filters,
        );
    const { data: attentionList } = useAuthorizationsAttentionList(
        dataOrganizationId,
        attentionModal,
        dateRange.from,
        dateRange.to,
        attentionPage,
        ATTENTION_PAGE_SIZE,
        { reason: TRACE_REASON_ATTENTION },
        filters,
    );
    const { data: leaderboard, isLoading: leaderboardFetching } =
        useAuthorizationsLeaderboard(
            dataOrganizationId,
            leaderboardView,
            { reason: TRACE_REASON_LEADERBOARD },
            filters,
        );

    const summaryLoading = !dataReady || summaryFetching;
    const rollupLoading = !dataReady || rollupFetching;
    const leaderboardLoading = !dataReady || leaderboardFetching;
    const currentSummary = dataReady ? summary : undefined;
    const currentRollup = dataReady ? rollup : undefined;
    const currentLeaderboard = dataReady ? leaderboard : undefined;

    const rollupRowsWithData = useMemo(() => {
        if (!currentRollup) {
            return [];
        }
        return currentRollup.rows.filter(
            (row) => toNumber(row.hours_authorized) > 0,
        );
    }, [currentRollup]);

    const trendData = useMemo(
        () =>
            [...rollupRowsWithData].reverse().map((row) => ({
                month: formatMonth(row.month_start),
                util: toNumber(row.auth_utilization_pct),
            })),
        [rollupRowsWithData],
    );

    const hasNoSyncData =
        currentSummary !== undefined && currentSummary.last_synced_at === null;

    function openAttention(type: AttentionType) {
        setAttentionPage(1);
        setAttentionModal(type);
    }

    return (
        <div className="flex h-full flex-col">
            <div className="border-b px-6 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight">
                            Authorizations
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Authorized vs. completed hours across your active
                            authorizations.
                        </p>
                    </div>
                    <span className="text-muted-foreground text-xs">
                        {formatRelativeSync(currentSummary?.last_synced_at)}
                    </span>
                </div>
                <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <DataTableToolbar
                            table={filterTable}
                            hideGlobalSearch={true}
                            hideColumnsButton={true}
                            facetedFilters={[
                                {
                                    columnId: 'locations',
                                    title: 'Location',
                                    icon: Building,
                                    limit: 50,
                                    predefinedOptions:
                                        filterOptions?.locations ?? [],
                                },
                                {
                                    columnId: 'states',
                                    title: 'State',
                                    icon: MapPin,
                                    predefinedOptions:
                                        filterOptions?.states ?? [],
                                },
                                {
                                    columnId: 'statuses',
                                    title: 'Status',
                                    icon: CircleDot,
                                    valueFormatter: (value) =>
                                        titleCase(String(value)),
                                    predefinedOptions:
                                        filterOptions?.statuses ?? [],
                                },
                                {
                                    columnId: 'service_lines',
                                    title: 'Service type',
                                    icon: Layers,
                                    predefinedOptions:
                                        filterOptions?.service_lines ?? [],
                                },
                            ]}
                        />
                    </div>
                    <div className="w-64 shrink-0">
                        <DateRangePicker
                            onChange={(value) => {
                                userAdjustedRange.current = true;
                                setDateRange(value);
                            }}
                            timezone={timezone}
                            value={dateRange}
                        />
                    </div>
                </div>
            </div>
            <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-6">
                {hasNoSyncData ? (
                    <Card>
                        <CardHeader>
                            <CardTitle>No authorizations synced yet</CardTitle>
                            <CardDescription>
                                The daily WellSky sync runs at 10:15 UTC. Once
                                the first sync completes, your dashboard will
                                populate automatically.
                            </CardDescription>
                        </CardHeader>
                    </Card>
                ) : (
                    <Fragment>
                        <NeedsAttentionPanel
                            isLoading={summaryLoading}
                            onOpenOverDelivered={() => {
                                openAttention('over_delivered');
                            }}
                            onOpenUnderScheduled={() => {
                                openAttention('under_scheduled');
                            }}
                            overDeliveredCount={
                                currentSummary?.over_delivered_count ?? 0
                            }
                            underScheduledCount={
                                currentSummary?.under_scheduled_count ?? 0
                            }
                        />
                        <section>
                            <h2 className="mb-3 text-lg font-semibold">
                                Period summary
                            </h2>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                                <KpiCard
                                    isLoading={summaryLoading}
                                    label="Hours Authorized"
                                    value={formatHours(
                                        currentSummary?.hours_authorized,
                                    )}
                                />
                                <KpiCard
                                    isLoading={summaryLoading}
                                    label="Hours on the Books"
                                    value={formatHours(
                                        currentSummary?.hours_on_books,
                                    )}
                                />
                                <KpiCard
                                    isLoading={summaryLoading}
                                    label="% Possible"
                                    value={formatPercent(
                                        currentSummary?.percent_possible,
                                    )}
                                    valueClassName={utilColorClass(
                                        toNumber(
                                            currentSummary?.percent_possible,
                                        ),
                                    )}
                                />
                                <KpiCard
                                    isLoading={summaryLoading}
                                    label="Hours Completed"
                                    value={formatHours(
                                        currentSummary?.hours_completed,
                                    )}
                                />
                                <KpiCard
                                    isLoading={summaryLoading}
                                    label="Auth Utilization"
                                    value={formatPercent(
                                        currentSummary?.auth_utilization_pct,
                                    )}
                                    valueClassName={utilColorClass(
                                        toNumber(
                                            currentSummary?.auth_utilization_pct,
                                        ),
                                    )}
                                />
                            </div>
                        </section>
                        <section>
                            <h2 className="mb-3 text-lg font-semibold">
                                Utilization trend
                            </h2>
                            <Card>
                                <CardContent className="pt-6">
                                    {rollupLoading || trendData.length === 0 ? (
                                        <div className="text-muted-foreground py-8 text-center text-sm">
                                            {rollupLoading
                                                ? 'Loading…'
                                                : 'No data yet.'}
                                        </div>
                                    ) : (
                                        <ResponsiveContainer
                                            height={260}
                                            width="100%"
                                        >
                                            <LineChart data={trendData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey="month"
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(
                                                        v: number,
                                                    ) => `${v.toFixed(0)}%`}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value) =>
                                                        `${toNumber(value as string | number).toFixed(1)}%`
                                                    }
                                                />
                                                <Line
                                                    dataKey="util"
                                                    dot={{ r: 3 }}
                                                    stroke="var(--chart-1)"
                                                    strokeWidth={2}
                                                    type="monotone"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </CardContent>
                            </Card>
                        </section>
                        <section>
                            <h2 className="mb-3 text-lg font-semibold">
                                Monthly rollup
                            </h2>
                            <Card>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Month</TableHead>
                                                <TableHead className="text-right">
                                                    Authorized
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    On the Books
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    % Possible
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    Completed
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    Auth Utilization
                                                </TableHead>
                                                <TableHead className="text-right">
                                                    % of On the Books
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {rollupRowsWithData.length === 0 ? (
                                                <TableRow>
                                                    <TableCell
                                                        className="text-muted-foreground py-6 text-center"
                                                        colSpan={7}
                                                    >
                                                        {rollupLoading
                                                            ? 'Loading…'
                                                            : 'No monthly data yet.'}
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                rollupRowsWithData.map(
                                                    (row) => {
                                                        const utilNum =
                                                            toNumber(
                                                                row.auth_utilization_pct,
                                                            );
                                                        const possibleNum =
                                                            toNumber(
                                                                row.percent_possible,
                                                            );
                                                        const ofOnBooksNum =
                                                            toNumber(
                                                                row.completed_of_on_books_pct,
                                                            );
                                                        return (
                                                            <TableRow
                                                                key={
                                                                    row.month_start
                                                                }
                                                            >
                                                                <TableCell className="font-medium">
                                                                    {formatMonth(
                                                                        row.month_start,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {formatHours(
                                                                        row.hours_authorized,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {formatHours(
                                                                        row.hours_on_books,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={cn(
                                                                        'text-right',
                                                                        utilColorClass(
                                                                            possibleNum,
                                                                        ),
                                                                    )}
                                                                >
                                                                    {formatPercent(
                                                                        row.percent_possible,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {formatHours(
                                                                        row.hours_completed,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={cn(
                                                                        'text-right',
                                                                        utilColorClass(
                                                                            utilNum,
                                                                        ),
                                                                    )}
                                                                >
                                                                    {formatPercent(
                                                                        row.auth_utilization_pct,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    className={cn(
                                                                        'text-right',
                                                                        utilColorClass(
                                                                            ofOnBooksNum,
                                                                        ),
                                                                    )}
                                                                >
                                                                    {formatPercent(
                                                                        row.completed_of_on_books_pct,
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    },
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </section>
                        <OfficeLeaderboardSection
                            currentPeriodEnd={
                                currentLeaderboard?.current_period_end ?? null
                            }
                            currentPeriodStart={
                                currentLeaderboard?.current_period_start ?? null
                            }
                            isLoading={leaderboardLoading}
                            onViewChange={setLeaderboardView}
                            previousPeriodEnd={
                                currentLeaderboard?.previous_period_end ?? null
                            }
                            previousPeriodStart={
                                currentLeaderboard?.previous_period_start ??
                                null
                            }
                            rows={currentLeaderboard?.rows ?? []}
                            view={leaderboardView}
                        />
                    </Fragment>
                )}
            </div>
            <AttentionListDialog
                hasMore={attentionList?.has_more ?? false}
                items={attentionList?.items ?? []}
                onClose={() => {
                    setAttentionModal(null);
                }}
                onNextPage={() => {
                    setAttentionPage((p) => p + 1);
                }}
                onPrevPage={() => {
                    setAttentionPage((p) => Math.max(1, p - 1));
                }}
                openType={attentionModal}
                page={attentionPage}
            />
        </div>
    );
}

type LeaderboardRow = {
    readonly location: string;
    readonly auth_count: number;
    readonly hours_authorized: string | number;
    readonly hours_completed: string | number;
    readonly current_auth_utilization_pct: string | number;
    readonly previous_auth_utilization_pct: string | number;
    readonly delta_pct: string | number;
};

type OfficeSortKey =
    | 'location'
    | 'auth_count'
    | 'hours_authorized'
    | 'hours_completed'
    | 'current_auth_utilization_pct'
    | 'previous_auth_utilization_pct'
    | 'delta_pct';
type OfficeSortDirection = 'asc' | 'desc';

type OfficeLeaderboardSectionProps = {
    readonly rows: ReadonlyArray<LeaderboardRow>;
    readonly isLoading: boolean;
    readonly view: 'month' | 'days';
    readonly onViewChange: (view: 'month' | 'days') => void;
    readonly currentPeriodStart: string | null;
    readonly currentPeriodEnd: string | null;
    readonly previousPeriodStart: string | null;
    readonly previousPeriodEnd: string | null;
};

function OfficeLeaderboardSection({
    rows,
    isLoading,
    view,
    onViewChange,
    currentPeriodStart,
    currentPeriodEnd,
    previousPeriodStart,
    previousPeriodEnd,
}: OfficeLeaderboardSectionProps) {
    const [sortKey, setSortKey] = useState<OfficeSortKey>(
        'current_auth_utilization_pct',
    );
    const [sortDirection, setSortDirection] =
        useState<OfficeSortDirection>('desc');

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            const comparison =
                sortKey === 'location'
                    ? a.location.localeCompare(b.location)
                    : toNumber(a[sortKey]) - toNumber(b[sortKey]);
            return sortDirection === 'asc' ? comparison : -comparison;
        });
        return copy;
    }, [rows, sortKey, sortDirection]);

    // Highlight best/worst by utilization value, independent of the current
    // sort, so the standout offices stay marked even when sorted by another
    // column. Only meaningful with more than one office.
    const { bestLocation, worstLocation } = useMemo(() => {
        if (rows.length < 2) {
            return { bestLocation: null, worstLocation: null };
        }
        let bestLocation: string | null = null;
        let worstLocation: string | null = null;
        let bestUtil = -Infinity;
        let worstUtil = Infinity;
        for (const row of rows) {
            const util = toNumber(row.current_auth_utilization_pct);
            if (util > bestUtil) {
                bestUtil = util;
                bestLocation = row.location;
            }
            if (util < worstUtil) {
                worstUtil = util;
                worstLocation = row.location;
            }
        }
        return { bestLocation, worstLocation };
    }, [rows]);

    const toggleSort = (nextKey: OfficeSortKey) => {
        if (nextKey === sortKey) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(nextKey);
        // Names read best A→Z; metrics read best high→low.
        setSortDirection(nextKey === 'location' ? 'asc' : 'desc');
    };

    // The label shows both concrete spans (previous month → current month).
    // Years are included only when the two periods land in different calendar
    // years.
    const showYear =
        previousPeriodStart !== null &&
        currentPeriodStart !== null &&
        previousPeriodStart.slice(0, 4) !== currentPeriodStart.slice(0, 4);
    const formatPeriod = (startIso: string, endIso: string) => {
        const fmt = (iso: string) =>
            new Date(`${iso}T00:00:00`).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                ...(showYear ? { year: 'numeric' } : {}),
            });
        return `${fmt(startIso)} – ${fmt(endIso)}`;
    };
    const periodLabel =
        previousPeriodStart &&
        previousPeriodEnd &&
        currentPeriodStart &&
        currentPeriodEnd
            ? `${formatPeriod(previousPeriodStart, previousPeriodEnd)} → ${formatPeriod(currentPeriodStart, currentPeriodEnd)}`
            : 'Previous period → selected period';

    return (
        <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Office leaderboard</h2>
                <div className="flex items-center gap-3">
                    <span className="text-muted-foreground text-xs">
                        {periodLabel}
                    </span>
                    <Tabs
                        onValueChange={(value) => {
                            if (value === 'month' || value === 'days') {
                                onViewChange(value);
                            }
                        }}
                        value={view}
                    >
                        <TabsList className="h-8">
                            <TabsTrigger value="month">Month</TabsTrigger>
                            <TabsTrigger value="days">Days</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>
            <Card>
                <CardContent className="p-0">
                    {/* Cap the height so the board stays scannable even with
                        many offices; the header sticks while the body scrolls.
                        Sorting still spans every office (client-side). */}
                    <div className="max-h-[28rem] overflow-y-auto">
                        <Table>
                            <TableHeader className="bg-background sticky top-0 z-10">
                                <TableRow>
                                    <TableHead className="w-12 text-right">
                                        #
                                    </TableHead>
                                    <OfficeSortHeader
                                        label="Office"
                                        onClick={() => toggleSort('location')}
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="location"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Auths"
                                        onClick={() => toggleSort('auth_count')}
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="auth_count"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Hrs Authorized"
                                        onClick={() =>
                                            toggleSort('hours_authorized')
                                        }
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="hours_authorized"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Hrs Completed"
                                        onClick={() =>
                                            toggleSort('hours_completed')
                                        }
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="hours_completed"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Util %"
                                        onClick={() =>
                                            toggleSort(
                                                'current_auth_utilization_pct',
                                            )
                                        }
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="current_auth_utilization_pct"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Prev period"
                                        onClick={() =>
                                            toggleSort(
                                                'previous_auth_utilization_pct',
                                            )
                                        }
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="previous_auth_utilization_pct"
                                    />
                                    <OfficeSortHeader
                                        align="right"
                                        label="Δ"
                                        onClick={() => toggleSort('delta_pct')}
                                        sortDirection={sortDirection}
                                        sortKey={sortKey}
                                        thisKey="delta_pct"
                                        tooltip="Percentage-point change in utilization vs the previous period"
                                    />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedRows.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            className="text-muted-foreground py-6 text-center"
                                            colSpan={8}
                                        >
                                            {isLoading
                                                ? 'Loading…'
                                                : 'No office data yet.'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedRows.map((row, index) => (
                                        <TableRow
                                            key={row.location}
                                            className={cn(
                                                row.location === bestLocation &&
                                                    'border-l-2 border-l-emerald-500',
                                                row.location ===
                                                    worstLocation &&
                                                    'border-l-2 border-l-red-500',
                                            )}
                                        >
                                            <TableCell className="text-muted-foreground text-right">
                                                {index + 1}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {row.location}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {row.auth_count.toLocaleString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatHours(
                                                    row.hours_authorized,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatHours(
                                                    row.hours_completed,
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className={cn(
                                                    'text-right font-medium',
                                                    utilColorClass(
                                                        toNumber(
                                                            row.current_auth_utilization_pct,
                                                        ),
                                                    ),
                                                )}
                                            >
                                                {formatPercent(
                                                    row.current_auth_utilization_pct,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-right">
                                                {formatPercent(
                                                    row.previous_auth_utilization_pct,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <DeltaCell
                                                    value={toNumber(
                                                        row.delta_pct,
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </section>
    );
}

type OfficeSortHeaderProps = {
    readonly label: string;
    readonly thisKey: OfficeSortKey;
    readonly sortKey: OfficeSortKey;
    readonly sortDirection: OfficeSortDirection;
    readonly onClick: () => void;
    readonly align?: 'left' | 'right';
    readonly tooltip?: string;
};

function OfficeSortHeader({
    label,
    thisKey,
    sortKey,
    sortDirection,
    onClick,
    align = 'left',
    tooltip,
}: OfficeSortHeaderProps) {
    const icon =
        sortDirection === 'asc' ? (
            <ArrowUp className="size-3" />
        ) : (
            <ArrowDown className="size-3" />
        );
    return (
        <TableHead className={cn(align === 'right' && 'text-right')}>
            <Button
                className={cn('h-7 px-1', align === 'right' && 'ml-auto')}
                onClick={onClick}
                size="sm"
                title={tooltip}
                variant="ghost"
            >
                {label}
                {sortKey === thisKey ? icon : null}
            </Button>
        </TableHead>
    );
}

function DeltaCell({ value }: { readonly value: number }) {
    if (value > 0) {
        return (
            <span className="inline-flex items-center justify-end gap-0.5 text-emerald-600">
                <ArrowUp className="size-3" />
                {`+${value.toFixed(1)} pp`}
            </span>
        );
    }
    if (value < 0) {
        return (
            <span className="inline-flex items-center justify-end gap-0.5 text-red-600">
                <ArrowDown className="size-3" />
                {`${value.toFixed(1)} pp`}
            </span>
        );
    }
    return <span className="text-muted-foreground">0.0 pp</span>;
}

type NeedsAttentionPanelProps = {
    readonly isLoading: boolean;
    readonly overDeliveredCount: number;
    readonly underScheduledCount: number;
    readonly onOpenOverDelivered: () => void;
    readonly onOpenUnderScheduled: () => void;
};

function NeedsAttentionPanel({
    isLoading,
    overDeliveredCount,
    underScheduledCount,
    onOpenOverDelivered,
    onOpenUnderScheduled,
}: NeedsAttentionPanelProps) {
    return (
        <section>
            <h2 className="mb-3 text-lg font-semibold">Needs attention</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <AttentionCard
                    accent="text-red-600"
                    count={overDeliveredCount}
                    description="Buckets where completed hours exceed authorized. Lost revenue if any are billable."
                    icon={<TrendingUp className="size-5" />}
                    isLoading={isLoading}
                    label="Over-delivered"
                    onClick={onOpenOverDelivered}
                />
                <AttentionCard
                    accent="text-amber-600"
                    count={underScheduledCount}
                    description="Buckets ending in the future with low utilization. Caregiver-coverage or client-upsell opportunity."
                    icon={<TrendingDown className="size-5" />}
                    isLoading={isLoading}
                    label="Under-scheduled"
                    onClick={onOpenUnderScheduled}
                />
            </div>
        </section>
    );
}

type AttentionCardProps = {
    readonly accent: string;
    readonly count: number;
    readonly description: string;
    readonly icon: ReactNode;
    readonly isLoading: boolean;
    readonly label: string;
    readonly onClick: () => void;
};

function AttentionCard({
    accent,
    count,
    description,
    icon,
    isLoading,
    label,
    onClick,
}: AttentionCardProps) {
    return (
        <button
            className="text-left"
            disabled={isLoading || count === 0}
            onClick={onClick}
            type="button"
        >
            <Card className="hover:bg-muted/40 h-full transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                    <div>
                        <CardDescription className="flex items-center gap-1.5">
                            <span className={accent}>{icon}</span>
                            <span>{label}</span>
                        </CardDescription>
                        <CardTitle className="mt-1 text-3xl tracking-tight">
                            {isLoading ? '…' : count}
                        </CardTitle>
                    </div>
                    {count > 0 ? (
                        <AlertTriangle
                            aria-hidden={true}
                            className={cn('size-5', accent)}
                        />
                    ) : null}
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground text-sm">
                        {description}
                    </p>
                    {count > 0 ? (
                        <p className="text-primary mt-2 text-sm font-medium">
                            View list →
                        </p>
                    ) : null}
                </CardContent>
            </Card>
        </button>
    );
}

type KpiCardProps = {
    readonly label: string;
    readonly value: string;
    readonly isLoading?: boolean;
    readonly valueClassName?: string;
};

function KpiCard({ label, value, isLoading, valueClassName }: KpiCardProps) {
    return (
        <Card className="gap-3">
            <CardHeader className="gap-1">
                <CardDescription>{label}</CardDescription>
                {isLoading === true ? (
                    <Skeleton className="h-9 w-24" />
                ) : (
                    <CardTitle
                        className={cn(
                            'text-3xl tracking-tight',
                            valueClassName,
                        )}
                    >
                        {value}
                    </CardTitle>
                )}
            </CardHeader>
        </Card>
    );
}

type AttentionItem = {
    readonly bucket_id: string;
    readonly auth_number: string;
    readonly service_line: string;
    readonly measure_type: string;
    readonly client_name: string;
    readonly client_external_id: string | null;
    readonly bucket_start: string;
    readonly bucket_end: string;
    readonly authorized_hours: string | number;
    readonly completed_hours: string | number;
    readonly scheduled_not_completed_hours: string | number;
    readonly cancelled_hours: string | number;
    readonly remaining_hours: string | number;
    readonly utilization_pct: string | number;
    readonly state: string;
};

type AttentionListDialogProps = {
    readonly openType: AttentionType | null;
    readonly items: ReadonlyArray<AttentionItem>;
    readonly hasMore: boolean;
    readonly page: number;
    readonly onClose: () => void;
    readonly onNextPage: () => void;
    readonly onPrevPage: () => void;
};

function AttentionListDialog({
    openType,
    items,
    hasMore,
    page,
    onClose,
    onNextPage,
    onPrevPage,
}: AttentionListDialogProps) {
    const title =
        openType === 'over_delivered'
            ? 'Over-delivered authorizations'
            : 'Under-scheduled authorizations';
    const measureSuffix = (item: AttentionItem) =>
        item.measure_type === 'visits' ? 'visits' : 'hrs';

    // When the user pages forward/backward, the scrollable list keeps the
    // previous scrollTop so a long page 1 leaves page 2 mid-scroll. Reset
    // to the top whenever the page number changes.
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [page]);

    return (
        <Dialog
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
            open={openType !== null}
        >
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {openType === 'over_delivered'
                            ? 'Buckets where completed exceeds authorized for the selected period.'
                            : 'Buckets ending in the future with remaining capacity.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto" ref={scrollRef}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Client</TableHead>
                                <TableHead>Auth #</TableHead>
                                <TableHead>Service</TableHead>
                                <TableHead>Period</TableHead>
                                <TableHead className="text-right">
                                    Authorized
                                </TableHead>
                                <TableHead className="text-right">
                                    Completed
                                </TableHead>
                                <TableHead className="text-right">
                                    Remaining
                                </TableHead>
                                <TableHead className="text-right">
                                    Util %
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        className="text-muted-foreground py-6 text-center"
                                        colSpan={8}
                                    >
                                        No authorizations match this filter.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((item) => (
                                    <TableRow key={item.bucket_id}>
                                        <TableCell>
                                            <div className="font-medium">
                                                {item.client_name}
                                            </div>
                                            {item.client_external_id ===
                                            null ? null : (
                                                <div className="text-muted-foreground font-mono text-xs">
                                                    {item.client_external_id}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {item.auth_number}
                                        </TableCell>
                                        <TableCell>
                                            {item.service_line}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            {formatBucketRange(
                                                item.bucket_start,
                                                item.bucket_end,
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatHours(item.authorized_hours)}{' '}
                                            {measureSuffix(item)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatHours(item.completed_hours)}{' '}
                                            {measureSuffix(item)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatHours(item.remaining_hours)}{' '}
                                            {measureSuffix(item)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {formatPercent(
                                                item.utilization_pct,
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex items-center justify-between pt-2">
                    <span className="text-muted-foreground text-xs">
                        Page {page}
                    </span>
                    <div className="flex gap-2">
                        <button
                            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                            disabled={page === 1}
                            onClick={onPrevPage}
                            type="button"
                        >
                            Previous
                        </button>
                        <button
                            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                            disabled={!hasMore}
                            onClick={onNextPage}
                            type="button"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
